import React from 'react';
import { useApp } from '../contexts/AppContext';
import { SEALS, DAY_ACCENT, PROGRAM_DAY_THEMES } from '../data/sealsData';

export default function StampGrid({ stamps = [], programId, programSlug = 'kids-entrepreneurship', compact = false }) {
  const { lang } = useApp();

  const getStamp = (day, session) =>
    stamps.find(s => s.program_id === programId && s.day_number === day && s.session === session);

  const totalEarned = stamps.filter(s => s.program_id === programId).length;
  const totalPossible = 10;
  const pct = Math.round((totalEarned / totalPossible) * 100);

  const themes = PROGRAM_DAY_THEMES[programSlug] || PROGRAM_DAY_THEMES['kids-entrepreneurship'];
  const DAYS_LABEL = lang === 'ja' ? themes.ja : themes.en;
  const dayAccent = themes.accent || DAY_ACCENT;

  return (
    <div>
      {/* Progress bar */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>
            {lang === 'ja' ? `🌟 シール ${totalEarned} / ${totalPossible}` : `🌟 Seals ${totalEarned} / ${totalPossible}`}
          </span>
          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: totalEarned === totalPossible ? '#06D6A0' : 'var(--orange)' }}>
            {pct}% {totalEarned === totalPossible ? (lang === 'ja' ? '🎉 コンプリート！' : '🎉 Complete!') : ''}
          </span>
        </div>
        <div style={{ height: 10, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: totalEarned === totalPossible
              ? 'linear-gradient(90deg, #06D6A0, #118AB2)'
              : 'linear-gradient(90deg, #FF6B35, #FFD166)',
            borderRadius: 999,
            transition: 'width 0.6s ease',
          }} />
        </div>
      </div>

      {/* Seal grid — 5 days */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 12 }}>
        {[1, 2, 3, 4, 5].map(dayNum => {
          const morningSeal = SEALS.find(s => s.day === dayNum && s.session === 'morning');
          const afternoonSeal = SEALS.find(s => s.day === dayNum && s.session === 'afternoon');
          const morningStamp = getStamp(dayNum, 'morning');
          const afternoonStamp = getStamp(dayNum, 'afternoon');
          const accent = dayAccent[dayNum] || DAY_ACCENT[dayNum];

          return (
            <div key={dayNum} style={{
              background: 'white',
              borderRadius: compact ? 12 : 16,
              border: `2px solid ${(morningStamp && afternoonStamp) ? accent + '40' : '#F1F5F9'}`,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}>
              {/* Day label */}
              <div style={{
                background: `${accent}15`,
                borderBottom: `2px solid ${accent}20`,
                padding: compact ? '0.5rem 0.85rem' : '0.6rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ fontWeight: 800, fontSize: compact ? '0.75rem' : '0.85rem', color: accent }}>
                  {lang === 'ja' ? `第${dayNum}日` : `Day ${dayNum}`}
                </span>
                <span style={{ fontSize: compact ? '0.7rem' : '0.78rem', color: '#64748b', fontWeight: 600 }}>
                  {DAYS_LABEL[dayNum - 1]}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: compact ? '0.7rem' : '0.75rem', fontWeight: 700, color: accent }}>
                  {(morningStamp ? 1 : 0) + (afternoonStamp ? 1 : 0)}/2
                </span>
              </div>

              {/* Two seals side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {[
                  { seal: morningSeal, stamp: morningStamp, sessionLabel: lang === 'ja' ? '午前' : 'Morning', icon: '☀️' },
                  { seal: afternoonSeal, stamp: afternoonStamp, sessionLabel: lang === 'ja' ? '午後' : 'Afternoon', icon: '🌅' },
                ].map(({ seal, stamp, sessionLabel, icon }, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: compact ? '0.75rem 0.5rem' : '1rem 0.75rem',
                      textAlign: 'center',
                      borderLeft: idx === 1 ? '1px solid #F1F5F9' : 'none',
                      background: stamp ? 'white' : '#FAFBFC',
                      transition: 'background 0.2s',
                    }}
                  >
                    {/* Seal circle */}
                    <div style={{
                      width: compact ? 52 : 68,
                      height: compact ? 52 : 68,
                      borderRadius: '50%',
                      margin: '0 auto 0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: compact ? '1.5rem' : '2rem',
                      background: stamp
                        ? seal.gradient
                        : 'linear-gradient(135deg, #E2E8F0, #CBD5E1)',
                      boxShadow: stamp
                        ? `0 6px 20px ${seal.shadow}`
                        : 'none',
                      border: stamp ? '3px solid rgba(255,255,255,0.5)' : '3px dashed #CBD5E1',
                      transform: stamp ? 'scale(1)' : 'scale(0.9)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      {stamp ? (
                        <>
                          {/* Shine effect */}
                          <div style={{
                            position: 'absolute', top: -10, left: -10,
                            width: '60%', height: '60%',
                            background: 'rgba(255,255,255,0.25)',
                            borderRadius: '50%',
                            filter: 'blur(4px)',
                          }} />
                          {seal.emoji}
                        </>
                      ) : (
                        <span style={{ fontSize: compact ? '1rem' : '1.2rem', opacity: 0.4 }}>🔒</span>
                      )}
                    </div>

                    {/* Seal name */}
                    <div style={{
                      fontWeight: 800,
                      fontSize: compact ? '0.65rem' : '0.78rem',
                      color: stamp ? '#1e293b' : '#94a3b8',
                      lineHeight: 1.2,
                      marginBottom: '0.2rem',
                    }}>
                      {stamp ? (lang === 'ja' ? seal.ja : seal.en) : (lang === 'ja' ? '？？？' : '???')}
                    </div>

                    {/* Session label */}
                    <div style={{
                      fontSize: compact ? '0.6rem' : '0.7rem',
                      fontWeight: 700,
                      color: stamp ? accent : '#cbd5e1',
                    }}>
                      {icon} {sessionLabel}
                    </div>

                    {/* Earned description — not in compact */}
                    {!compact && stamp && (
                      <div style={{
                        marginTop: '0.4rem',
                        fontSize: '0.65rem',
                        color: '#64748b',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        fontStyle: 'italic',
                      }}>
                        {lang === 'ja' ? seal.descJa : seal.descEn}
                      </div>
                    )}

                    {/* Coming Soon badge if not earned */}
                    {!stamp && !compact && (
                      <div style={{
                        marginTop: '0.35rem',
                        fontSize: '0.58rem',
                        fontWeight: 800,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {lang === 'ja' ? 'もうすぐ！' : 'Coming Soon'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
